import { useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "../i18n";
import { api, fileUrl, formatApiError } from "../lib/api";

export default function DownloadGate({ open, onOpenChange, file, projectId }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", firm: "", email: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, project_id: projectId || "", file_name: file?.name || "" });
      localStorage.setItem("ai_gate", JSON.stringify({ name: form.name, firm: form.firm, email: form.email }));
      const a = document.createElement("a");
      a.href = fileUrl(file.storage_path, true);
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(t.gate_success);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="download-gate-modal" className="bg-[#0F0F0E] border-[#C8A86B]/25 text-[#F3EFE6] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">{t.gate_title}</DialogTitle>
          <DialogDescription className="text-sm text-[#A39E93] font-light">{t.gate_sub}</DialogDescription>
        </DialogHeader>
        {file && (
          <div className="flex items-center gap-3 border border-[#262420] bg-[#141413] px-4 py-3">
            <FileDown size={18} className="text-[#C8A86B] shrink-0" />
            <div className="min-w-0">
              <p className="truncate text-sm text-[#F3EFE6]">{file.name}</p>
              <p className="text-xs font-mono text-[#827D72] uppercase">{file.ext} · {file.size}</p>
            </div>
          </div>
        )}
        <form onSubmit={submit} className="flex flex-col gap-4 mt-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gate-name" className="text-xs font-mono uppercase tracking-wider text-[#827D72]">{t.gate_name}</Label>
            <Input id="gate-name" data-testid="gate-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#080808] border-[#262420] focus-visible:ring-[#C8A86B]" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gate-firm" className="text-xs font-mono uppercase tracking-wider text-[#827D72]">{t.gate_firm}</Label>
            <Input id="gate-firm" data-testid="gate-firm-input" value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} className="bg-[#080808] border-[#262420] focus-visible:ring-[#C8A86B]" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gate-email" className="text-xs font-mono uppercase tracking-wider text-[#827D72]">{t.gate_email}</Label>
            <Input id="gate-email" data-testid="gate-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#080808] border-[#262420] focus-visible:ring-[#C8A86B]" />
          </div>
          <button
            type="submit"
            data-testid="email-gate-submit"
            disabled={loading}
            className="mt-2 bg-[#C8A86B] px-6 py-3 text-sm uppercase tracking-[0.15em] text-[#080808] transition-colors duration-300 hover:bg-[#D8BD85] disabled:opacity-50"
          >
            {loading ? "…" : t.gate_submit}
          </button>
          <p className="text-center text-xs font-mono text-[#666157]">{t.gate_note}</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
